import qs from 'query-string';

import {
	ConfigurationDto,
	LeagueDto,
	PromotionItemDto,
	SportDto,
	SelectionDto,
	FeaturedContentDto,
	SearchResultDto,
	FeaturedContentTypeDto,
	SportPreferredMarketTypeDto,
	RecommendedBetDto,
	FeaturedPreCannedItemDto,
	OptixTrackSearchResultRequest,
	BetGeniusStreamingConfigurationDto,
	BetGeniusStreamingDataDto,
	QuickLinkDto,
	MarketGroupDto,
	MatchBetDto,
	LuckyBetItemDto,
	BetradarLiveStreamDto
} from './sbapi-api-model';
import reportSportsbookError from '../../utils/sportsbook-error-reporter';
import { isApiError, SerializableParameter } from './sbapi-client-types';
import { buildRoute, createTimeoutSignal, parseResponse } from './request-utils';

const ABORT_ERROR_NAME = 'AbortError';
const TIMEOUT_ERROR_NAME = 'TimeoutError';
const TIMEOUT_DEFAULT_MS = 10_000;

export type SearchFilters = {
	eventTypes?: string[];
	sportIds?: string[];
};

export default class SBApiClient {
	private baseUrl: string;

	private franchiseCode: string;

	private locale: string;

	constructor(baseUrl: string, franchiseCode: string, locale: string) {
		this.baseUrl = baseUrl;
		this.franchiseCode = franchiseCode;
		this.locale = locale;
	}

	public getBaseUrl = () => this.baseUrl;

	public getFranchiseCode = () => this.franchiseCode;

	public getLocale = () => this.locale;

	private withParams = (path: string, parameters?: { [ref: string]: SerializableParameter }): string => {
		const queryString = parameters && qs.stringify(parameters, { encode: true });
		return `${this.baseUrl}${path}${queryString ? `?${queryString}` : ''}`;
	};

	public async request<T>(
		path: string,
		parameters?: { [ref: string]: SerializableParameter },
		options?: { timeoutMs?: number }
	): Promise<T> {
		const route = buildRoute(this.baseUrl, path, parameters, this.franchiseCode, this.locale);

		const timeoutMs = options?.timeoutMs ?? TIMEOUT_DEFAULT_MS;
		const { signal, cleanup } = createTimeoutSignal(timeoutMs);

		try {
			const response = await fetch(route, { signal });
			return await parseResponse<T>(response, path);
		} catch (err) {
			if (err instanceof Error && (err.name === ABORT_ERROR_NAME || err.name === TIMEOUT_ERROR_NAME)) {
				throw new Error(`Request to ${path} timed out after ${timeoutMs}ms`);
			}
			throw err;
		} finally {
			cleanup();
		}
	}

	/**
	 * Executes a POST request to SBApi.
	 *
	 * @template V The type of the data being sent in the request.
	 * @template T The expected return type of the request.
	 *
	 * @param {string} path The URL path to which the request is sent.
	 * @param {V} data The data to be sent in the request body.
	 * @param {{ [ref: string]: SerializableParameter }} [parameters] Optional parameters to be appended to the URL.
	 * @param {AbortSignal} [abortSignal] Optional signal to abort the request.
	 *
	 * @returns {Promise<T>} A promise that resolves with the response of the request.
	 * @throws {Error} Throws an error if the response indicates an API error or if there is a fetch error.
	 */
	private postRequest = async <V, T>(
		path: string,
		data: V,
		parameters?: { [ref: string]: SerializableParameter },
		abortSignal?: AbortSignal
	): Promise<T> => {
		const bodyAsString = JSON.stringify(data);
		const defaultParameters = {
			franchiseCode: this.franchiseCode,
			locale: this.locale
		};
		const headers = {
			method: 'POST',
			mode: 'same-origin',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json'
			},
			redirect: 'follow',
			referrerPolicy: 'same-origin',
			body: bodyAsString,
			signal: abortSignal
		};

		const route = this.withParams(path, { ...defaultParameters, ...parameters });

		const response = <Promise<T>>await fetch(route, headers)
			.then(_response => _response.json())
			.catch(error => {
				// Do not report aborted requests
				if (!(error instanceof Error) || error.name !== ABORT_ERROR_NAME) {
					reportSportsbookError(`${path} error`, error, { urlPath: path, body: bodyAsString });
				}

				throw error;
			});

		if (isApiError(response)) {
			throw new Error(response.error);
		}

		return response;
	};

	public async fetchLeague(leagueId: string) {
		return this.request<LeagueDto>(`/api/leagues/${leagueId}`);
	}

	public async fetchLeagues(
		sportId?: string,
		leagueIds?: Array<string>,
		isLive?: boolean,
		isOutrightsOnlyLeague?: boolean,
		startingAfter?: string,
		startingBefore?: string,
		hasOutrights?: boolean
	) {
		return this.request<LeagueDto[]>('/api/leagues', {
			sportId,
			leagueIds,
			isLive,
			isOutrightsOnlyLeague,
			startingAfter,
			startingBefore,
			hasOutrights
		});
	}

	public async fetchSports() {
		const sports = await this.request<SportDto[]>('/api/sports');
		return sports.sort((a, b) => {
			if (a.order === b.order) {
				return a.name.localeCompare(b.name);
			}

			return a.order - b.order;
		});
	}

	public async fetchSport(sportId: string) {
		return this.request<SportDto>(`/api/sports/${sportId}`);
	}

	public async fetchSportDaysWithEvents(sportId: string, fromDate: string, toDate: string, timeZone: string) {
		return this.request<string[]>(`/api/sports/${sportId}/days-with-events`, {
			fromDate,
			toDate,
			timeZone
		});
	}

	public async fetchSelections(selectionIds: string[], isLocaleFallbackEnabled = true) {
		return this.request<SelectionDto[]>(`/api/selections`, {
			selectionIds,
			isLocaleFallbackEnabled
		});
	}

	public async fetchPromotionItems() {
		return this.request<PromotionItemDto[]>('/api/v2/promotions');
	}

	public async fetchFeaturedPreCannedItems(sportId?: string, leagueId?: string) {
		return this.request<FeaturedPreCannedItemDto[]>('/api/featuredPreCannedItems', {
			sportId,
			leagueId
		});
	}

	public async fetchBetRecommendations(key: string) {
		return this.request<Array<RecommendedBetDto>>(`/api/recommendations/bets`, {
			key
		});
	}

	public async fetchSearchResult(
		query: string,
		{ eventTypes, sportIds }: SearchFilters,
		isOptixSearchEnabled: boolean
	) {
		const path = isOptixSearchEnabled ? '/api/v4/search' : '/api/v3/search';

		return this.request<SearchResultDto>(path, {
			query,
			eventTypes,
			sportIds
		});
	}

	public async fetchOperatorConfigurations() {
		return this.request<ConfigurationDto>('/api/configuration');
	}

	public async fetchFeaturedLeagues() {
		return this.request<LeagueDto[]>('/api/leagues/featured');
	}

	public async fetchSelectionsByReference(referenceIds: string[], operatorId: string | null) {
		return this.request<Array<SelectionDto>>(`/api/selections/by-reference`, {
			referenceIds,
			operatorId: operatorId || undefined
		});
	}

	public async fetchFeaturedContent(types: FeaturedContentTypeDto[]) {
		return this.request<Array<FeaturedContentDto>>(`/api/featuredContent`, {
			types
		});
	}

	public fetchBetGeniusStreamingConfiguration() {
		return this.request<BetGeniusStreamingConfigurationDto>('/api/streaming/betgenius/configuration');
	}

	public async fetchBetGeniusStreamingData(
		fixtureId: string,
		streamId: string,
		deliveryType: string,
		deliveryId: string,
		device: string,
		region: string,
		anonymousSessionId: string
	) {
		return this.request<BetGeniusStreamingDataDto>(`/api/streaming/betgenius/live-stream`, {
			fixtureId,
			streamId,
			deliveryType,
			deliveryId,
			device,
			region,
			anonymousSessionId
		});
	}

	public async fetchBetradarStreamingData(streamId: string, streamType: string) {
		return this.request<BetradarLiveStreamDto>(`/api/streaming/betradar/live-stream`, {
			streamId,
			streamType
		});
	}

	public async fetchPreferredMarketTypesForSport(sportId: string) {
		return this.request<Array<SportPreferredMarketTypeDto>>('/api/markets/main-market-types', {
			sportId
		});
	}

	public async fetchDefaultMarketsForSports(sportIds?: string[]) {
		return this.request<Record<string, Array<Omit<SportPreferredMarketTypeDto, 'marketNameTextKey'>>>>(
			'/api/default-markets',
			{
				sportIds
			}
		);
	}

	public async fetchMatchBets(sportId: string) {
		return this.request<Array<MatchBetDto>>('/api/match-bets', {
			sportId
		});
	}

	public async trackOptixSearchResult(requestBody: OptixTrackSearchResultRequest) {
		return this.postRequest<OptixTrackSearchResultRequest, void>('/api/v2/search/track', requestBody);
	}

	public async fetchQuickLinks() {
		return this.request<QuickLinkDto[]>('/api/quick-links');
	}

	public async fetchMarketGroupsForEvent(eventId: string) {
		return this.request<MarketGroupDto[]>(`/api/events/${eventId}/market-groups`);
	}

	public async fetchLuckyBets(
		sportIds: Array<string>,
		count: string,
		minTotalOdds: string,
		maxTotalOdds: string,
		excludeEventIds?: string
	) {
		return this.request<LuckyBetItemDto[]>(`/api/lucky-bet/items`, {
			sportIds,
			count,
			minTotalOdds,
			maxTotalOdds,
			excludeEventIds
		});
	}
}

// SBApiClient singleton
let sbApiClient: SBApiClient | null = null;

export const getSBApiClient = () => {
	if (sbApiClient == null) {
		throw new Error('Sportsbook Api client has not been configured');
	}
	return sbApiClient;
};

export const createSBApiClient = (baseUrl: string, franchiseCode: string, locale: string) => {
	if (
		!sbApiClient ||
		sbApiClient.getBaseUrl() !== baseUrl ||
		sbApiClient.getFranchiseCode() !== franchiseCode ||
		sbApiClient?.getLocale() !== locale
	) {
		sbApiClient = new SBApiClient(baseUrl, franchiseCode, locale);
	}
};

// HMR for Singleton
// add the old client when disposing the module
// and then create a new one with the same settings
if (import.meta && import.meta.webpackHot) {
	import.meta.webpackHot.dispose(data => {
		// eslint-disable-next-line
		data.sbApiClient = sbApiClient;
	});

	// eslint-disable-next-line
	if (import.meta.webpackHot.data?.sbApiClient) {
		const oldClient = <
			SBApiClient // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		>import.meta.webpackHot.data.sbApiClient;
		createSBApiClient(oldClient.getBaseUrl(), oldClient.getFranchiseCode(), oldClient.getLocale());
	}
}
